import React, {useState, useEffect, useMemo} from 'react';
import {
    Box,
    Card,
    CardBody,
    Text,
    Input,
    Button,
    Grid,
    GridItem,
    VStack,
    HStack,
    Stack,
    Spinner,
    CardHeader,
    Tabs, TabList, TabPanels, Tab, TabPanel,
    SimpleGrid, Flex, Skeleton,
} from '@chakra-ui/react';
import {
    LineChart, Line, CartesianGrid, XAxis, YAxis, Tooltip,
    BarChart, Bar, Legend, ResponsiveContainer, AreaChart, Area
} from 'recharts';
import {FaUser, FaHeart, FaComment, FaInstagram, FaSave, FaPlay, FaHandshake} from 'react-icons/fa';
import {useParams,useNavigate} from 'react-router-dom';
import {customAuthAxios} from "@/utils/customAuthAxios";
import InstagramDMSection from './components/InstagramDMSection';
import MetricLineChart from './components/MetricLineChart';
import {useCallback} from 'react';
import {
    fetchProfileMetrics,
    fetchMediaData,
    getDjangoToken,
    saveAuthData,
    fetchAuthDataFromDjango
} from '@/utils/instagram/services/instagramService';
// 新しい共通コンポーネントをインポート
import InstagramPostsGrid, {MediaPost} from './components/InstagramPostsGrid';
import InstagramBulkDmSection from './components/InstagramBulkDmSection';
import {SmallCardWithIcon} from "../../molecules/card/SmallCardWithIcon";
import {ImUser} from "react-icons/im";


const Instagram: React.FC = () => {
    const navigate = useNavigate();
    // 既存の状態変数
    const {restaurantId} = useParams<{ restaurantId: string }>();
    const [isLoading, setIsLoading] = useState(true);
    const [isAuthenticated, setIsAuthenticated] = useState(false);
    const [accessToken, setAccessToken] = useState<string | null>(null);
    const [businessAccountId, setBusinessAccountId] = useState<string | null>(null);
    const [pageId, setPageId] = useState<string | null>(null);
    const [mediaData, setMediaData] = useState<any[]>([]);
    // 新しいステートを追加
    const [filteredMediaData, setFilteredMediaData] = useState<MediaPost[]>([]);
    const [metricsHistory, setMetricsHistory] = useState<any[]>([]);
    const latestReach = metricsHistory.at(-1)?.total_reach ?? 0;
    const latestImpressions = metricsHistory.at(-1)?.total_impressions ?? 0;
    const latestFollowerCount = metricsHistory.at(-1)?.follower_count ?? 0;
    const [instagramUserInfo, setInstagramUserInfo] = useState<{ username: string; id: string } | null>(null);

    // メトリクス情報のステート
    const [metrics, setMetrics] = useState<{
        followerCount: number;
        followingCount: number;
        postCount: number;
        totalLikes: number;
        totalComments: number;
        totalSaved: number;
        totalViews: number; // 表示回数
    } | null>(null);

    // メディアタイプ別統計のステート
    const [mediaTypeStats, setMediaTypeStats] = useState({
        image: {posts: 0, likes: 0, comments: 0},
        video: {posts: 0, views: 0, likes: 0, comments: 0},
        reel: {posts: 0, views: 0, likes: 0, comments: 0},
        story: {posts: 0, views: 0, likes: 0, comments: 0},
    });

    // フィルタリング機能をInstagram.tsxに追加
    const [displayMode, setDisplayMode] = useState<'images' | 'videos'>('images');
    const [dateRange, setDateRange] = useState<{ start: string; end: string }>(() => {
        const today = new Date();
        const oneMonthAgo = new Date();
        oneMonthAgo.setMonth(today.getMonth() - 1);

        return {
            start: oneMonthAgo.toISOString().split('T')[0],
            end: today.toISOString().split('T')[0]
        };
    });

    // 初期フィルタリング適用のためのuseEffectを追加
    useEffect(() => {
        // コンポーネントマウント時に初期フィルタリングを適用
        if (mediaData && mediaData.length > 0) {
            applyFilters();
        }
    }, [mediaData]);


    // フィルター変更ハンドラー
    const handleFilterChange = (mode: 'images' | 'videos') => {
        setDisplayMode(mode);
    };

    const handleDateRangeChange = (start: string, end: string) => {
        setDateRange({start, end});
    };
// フィルタリングロジック
    const applyFilters = useCallback(() => {
        if (!mediaData || mediaData.length === 0) {
            setFilteredMediaData([]);
            return;
        }

        const filtered = mediaData
            .filter(post => {
                const type = post.media_type?.toUpperCase() || '';
                const isCorrectType = displayMode === 'images'
                    ? (type === 'IMAGE' || type === 'CAROUSEL_ALBUM')
                    : (type === 'VIDEO' || type === 'REEL');

                if (!isCorrectType) return false;

                if (!dateRange.start && !dateRange.end) return true;
                if (!post.timestamp) return true;

                try {
                    const postDate = new Date(post.timestamp);
                    return (!dateRange.start || postDate >= new Date(dateRange.start)) &&
                        (!dateRange.end || postDate <= new Date(dateRange.end));
                } catch (e) {
                    console.error('日付変換エラー:', e);
                    return true;
                }
            })
            .map(item => ({
                id: item.id || '',
                media_type: item.media_type || 'IMAGE',
                media_url: item.media_url || '',
                permalink: item.permalink || '',
                caption: item.caption || '',
                timestamp: item.timestamp || new Date().toISOString(),
                impressions: typeof item.impressions === 'number' ? item.impressions : 0,
                like_count: typeof item.like_count === 'number' ? item.like_count :
                    (typeof item.likes === 'number' ? item.likes : 0),
                comments_count: typeof item.comments_count === 'number' ? item.comments_count :
                    (typeof item.comments === 'number' ? item.comments : 0),
                saved: typeof item.saved === 'number' ? item.saved : 0,
                reach: typeof item.reach === 'number' ? item.reach : 0
            }));

        console.log('フィルタリング後のデータ:', filtered);
        setFilteredMediaData(filtered);
    }, [mediaData, displayMode, dateRange]);

    const [loginMethod, setLoginMethod] = useState<'facebook' | 'instagram'>('facebook');

    const fetchInstagramData = useCallback(async (
        token?: string,
        instagramId?: string,
        source: 'facebook' | 'instagram' = 'facebook'
    ) => {
        const currentToken = token || accessToken;
        const currentInstagramId = instagramId || businessAccountId;

        if (!currentToken || !currentInstagramId) return null;

        try {
            const djangoToken = await getDjangoToken();
            const authRes = await customAuthAxios.get(
                `external_api/instagram-auth/${restaurantId}/`,
                {headers: {Authorization: `Bearer ${djangoToken}`}}
            );

            const pageId = authRes.data.page_id;
            const authSource = authRes.data.source || source;


            if (!pageId) {
                console.warn("ページIDが取得できませんでした（Djangoレスポンス）- DMの機能に影響する可能性があります");
            }

            let profileMetrics = {followerCount: 0, followingCount: 0, username: '', id: ''};
            if (authSource === 'facebook') {
                profileMetrics = await fetchProfileMetrics(currentInstagramId, currentToken);

                // console.log("プロフィールメトリクス:", profileMetrics);
                // console.log("フォロワー数:", profileMetrics.followerCount);
                // console.log("フォロー数:", profileMetrics.followingCount);

                setInstagramUserInfo({
                    username: profileMetrics.username,
                    id: profileMetrics.id
                });

                // まずここでsetMetricsを呼び出す
                setMetrics({
                    followerCount: profileMetrics.followerCount,
                    followingCount: profileMetrics.followingCount,
                    postCount: 0, // 後で上書き
                    totalLikes: 0,
                    totalComments: 0,
                    totalSaved: 0,
                    totalViews: 0,
                });
            } else {
                console.log("⚠ Instagramログインのためプロフィール情報は取得しません");
            }

            // メディアデータ取得部分
            const media = await fetchMediaData(currentInstagramId, currentToken, authSource);
            console.log("メディアデータ:", media, "タイプ:", typeof media, "配列?:", Array.isArray(media));

            if (!media || !Array.isArray(media) || media.length === 0) {
                console.warn("メディアデータが空または配列ではありません");
                setIsAuthenticated(true);
                setMediaData([]);
                return [];
            }

// 統計計算部分を修正
const totalLikes = media.reduce((sum: number, post: any) => sum + (post.like_count || 0), 0);
const totalComments = media.reduce((sum: number, post: any) => sum + (post.comments_count || 0), 0);
const totalSaved = media.reduce((sum: number, post: any) => sum + (post.saved || 0), 0);
const totalViews = media.reduce((sum: number, post: any) => sum + (post.views || 0), 0);

// デバッグログを追加
console.log("統計計算結果:");
console.log("総いいね数:", totalLikes);
console.log("総コメント数:", totalComments);
console.log("総保存数:", totalSaved);
console.log("総視聴数:", totalViews);

// メディアタイプ別統計も修正
const mediaByType = {
    IMAGE: media.filter((post: any) => post.media_type === 'IMAGE' || post.media_type === 'CAROUSEL_ALBUM'),
    VIDEO: media.filter((post: any) => post.media_type === 'VIDEO'),
    REEL: media.filter((post: any) => post.media_type === 'REEL'),
    STORY: media.filter((post: any) => post.media_type === 'STORY'),
};

const statsByType = {
    image: {
        posts: mediaByType.IMAGE.length,
        likes: mediaByType.IMAGE.reduce((sum: number, post: any) => sum + (post.like_count || 0), 0),
        comments: mediaByType.IMAGE.reduce((sum: number, post: any) => sum + (post.comments_count || 0), 0),
    },
    video: {
        posts: mediaByType.VIDEO.length,
        views: mediaByType.VIDEO.reduce((sum: number, post: any) => sum + (post.views || 0), 0),
        likes: mediaByType.VIDEO.reduce((sum: number, post: any) => sum + (post.like_count || 0), 0),
        comments: mediaByType.VIDEO.reduce((sum: number, post: any) => sum + (post.comments_count || 0), 0),
    },
    reel: {
        posts: mediaByType.REEL.length,
        views: mediaByType.REEL.reduce((sum: number, post: any) => sum + (post.views || 0), 0),
        likes: mediaByType.REEL.reduce((sum: number, post: any) => sum + (post.like_count || 0), 0),
        comments: mediaByType.REEL.reduce((sum: number, post: any) => sum + (post.comments_count || 0), 0),
    },
    story: {
        posts: mediaByType.STORY.length,
        views: mediaByType.STORY.reduce((sum: number, post: any) => sum + (post.views || 0), 0),
        likes: mediaByType.STORY.reduce((sum: number, post: any) => sum + (post.like_count || 0), 0),
        comments: mediaByType.STORY.reduce((sum: number, post: any) => sum + (post.comments_count || 0), 0),
    },
};

setMediaTypeStats(statsByType);

// 最終的なsetMetricsでprofileMetricsの値を保持
console.log("setMetrics呼び出し前 - profileMetrics:", profileMetrics);
console.log("setMetrics呼び出し前 - followerCount:", profileMetrics.followerCount);
console.log("setMetrics呼び出し前 - followingCount:", profileMetrics.followingCount);

setMetrics({
    followerCount: profileMetrics.followerCount,
    followingCount: profileMetrics.followingCount,
    postCount: media.length,
    totalLikes,
    totalComments,
    totalSaved,
    totalViews,
});

            setIsAuthenticated(true);
            setMediaData(media);

            await saveAuthData(
                restaurantId!,
                currentToken,
                currentInstagramId,
                djangoToken
            );

            return media;
        } catch (error) {
            console.error("Error fetching Instagram data:", error);
            return null;
        }
    }, [restaurantId, accessToken, businessAccountId]);

    const fetchAuthData = useCallback(async () => {
        if (!restaurantId) return;

        try {
            setIsLoading(true);
            const authData = await fetchAuthDataFromDjango(restaurantId);

            if (authData?.token && authData?.instagram_id) {
                setAccessToken(authData.token);
                setBusinessAccountId(authData.instagram_id);
                setPageId(authData.page_id || null);

                setLoginMethod(authData.source === 'instagram' ? 'instagram' : 'facebook');
                setIsAuthenticated(true);

                await fetchInstagramData(authData.token, authData.instagram_id, authData.source); // ← ここで使ってる
            } else {
                setIsAuthenticated(false);
            }

            if (Array.isArray(authData.metrics)) {
                setMetricsHistory(authData.metrics.slice(-6));
            }
        } catch (error) {
            console.error("❌ Instagram認証情報取得エラー:", error);
            setIsAuthenticated(false);
        } finally {
            setIsLoading(false);
        }
    }, [restaurantId, fetchInstagramData]);


    const monthlyInteractions = useMemo(() => {
        const grouped = new Map<string, number>();
        metricsHistory.forEach(item => {
            const month = new Date(item.timestamp).toLocaleDateString('ja-JP', {
                month: 'long',
                day: 'numeric'
            });
            const total = (item.total_likes || 0) + (item.total_comments || 0);
            grouped.set(month, (grouped.get(month) || 0) + total);
        });

        return Array.from(grouped.entries()).map(([month, interactions]) => ({month, interactions}));
    }, [metricsHistory]);

    const filteredMetricsHistory = useMemo(() => {
        if (!dateRange.start && !dateRange.end) return metricsHistory;

        return metricsHistory.filter(item => {
            const timestamp = new Date(item.timestamp);
            const startDate = dateRange.start ? new Date(dateRange.start) : null;
            const endDate = dateRange.end ? new Date(dateRange.end) : null;

            return (!startDate || timestamp >= startDate) && (!endDate || timestamp <= endDate);
        });
    }, [metricsHistory, dateRange]);

    const mediaTypeChartData = useMemo(() => [
        {
            type: '画像',
            likes: mediaTypeStats.image.likes,
            comments: mediaTypeStats.image.comments
        },
        {
            type: '動画',
            likes: mediaTypeStats.video.likes,
            comments: mediaTypeStats.video.comments
        },
        {
            type: 'リール',
            likes: mediaTypeStats.reel.likes,
            comments: mediaTypeStats.reel.comments
        },
        {
            type: 'ストーリー',
            likes: mediaTypeStats.story.likes,
            comments: mediaTypeStats.story.comments
        }
    ], [mediaTypeStats]);

    // フィルタリング適用
    useEffect(() => {
        applyFilters();
    }, [applyFilters]);

    // 既存のuseEffect - そのまま維持
    useEffect(() => {
        fetchAuthData();
    }, [fetchAuthData]);

    // 新しいuseEffect - mediaDataをMediaPost型に変換
    useEffect(() => {
        if (mediaData && mediaData.length > 0) {
            console.log('Instagram.tsx: mediaDataを変換中', mediaData);

            // 重要: MediaPostの型に適切にマッピング
            const formattedData = mediaData.map(item => {
                // コンソールでデータの構造を確認
                // console.log('元のアイテム:', item);

                // MediaPost型に合わせて適切に変換
                const post: MediaPost = {
                    id: item.id || '',
                    media_type: item.media_type || 'IMAGE',
                    media_url: item.media_url || '',
                    permalink: item.permalink || '',
                    caption: item.caption || '',
                    timestamp: item.timestamp || new Date().toISOString(),

                    // 数値データ
                    impressions: typeof item.impressions === 'number' ? item.impressions : 0,
                    like_count: typeof item.like_count === 'number' ? item.like_count :
                        (typeof item.likes === 'number' ? item.likes : 0),
                    comments_count: typeof item.comments_count === 'number' ? item.comments_count :
                        (typeof item.comments === 'number' ? item.comments : 0),
                    saved: typeof item.saved === 'number' ? item.saved : 0,
                    reach: typeof item.reach === 'number' ? item.reach : 0
                };

                return post;
            });

            console.log('Instagram.tsx: 変換後のデータ', formattedData);
            setFilteredMediaData(formattedData);
        } else {
            console.log('Instagram.tsx: mediaDataが空です');
            setFilteredMediaData([]);
        }
    }, [mediaData]);

    const [tempDateRange, setTempDateRange] = useState(() => {
        const today = new Date();
        const oneMonthAgo = new Date();
        oneMonthAgo.setMonth(today.getMonth() - 1);

        return {
            start: oneMonthAgo.toISOString().split('T')[0], // YYYY-MM-DD形式
            end: today.toISOString().split('T')[0]
        };
    });


    const handleApply = () => {
        handleDateRangeChange(tempDateRange.start, tempDateRange.end);
    };

    const handleClear = () => {
        setTempDateRange({start: '', end: ''});
        handleDateRangeChange('', '');
    };


    return (
        <Box p={4}>
            {/*<HStack spacing={4} mb={4}>*/}
            {/*    <Button*/}
            {/*        colorScheme={loginMethod === 'facebook' ? 'blue' : 'gray'}*/}
            {/*        onClick={() => setLoginMethod('facebook')}*/}
            {/*    >*/}
            {/*        Facebookログイン*/}
            {/*    </Button>*/}
            {/*    <Button*/}
            {/*        colorScheme={loginMethod === 'instagram' ? 'pink' : 'gray'}*/}
            {/*        onClick={() => setLoginMethod('instagram')}*/}
            {/*    >*/}
            {/*        Instagramログイン*/}
            {/*    </Button>*/}
            {/*</HStack>*/}
            <VStack align={"left"}>
                <Text fontSize="md" mb={6} color={"gray"}>
                    {instagramUserInfo ? `${instagramUserInfo.username}（Instagram ID: ${instagramUserInfo.id}）` : '概要'}
                </Text>

                <HStack spacing={2} mb={4}>
                    <FaInstagram size={24}/>
                    <Text fontSize="2xl" fontWeight="bold">Instagramインサイト</Text>
                </HStack>
            </VStack>

            {isLoading ? (
                <VStack spacing={4} p={4} w="full">
                    <Spinner size="xl"/>
                    <Text>読み込み中...</Text>
                </VStack>
            ) : !isAuthenticated ? (
                <Card>
                    <CardBody>
                        <Text>Instagramアカウントが連携されていないかデータが受信できません。お手数ですが営業にご連絡ください。</Text>
                    </CardBody>
                </Card>
            ) : (
                <VStack spacing={4} align="stretch">
                    {/* メトリクス表示 */}
                    {metrics && loginMethod === 'facebook' && (
                        <VStack spacing={4} align="stretch">
                            <HStack spacing={2} mb={1} p={2} borderRadius="md">
                                <Input
                                    type="date"
                                    value={tempDateRange.start}
                                    onChange={(e) => setTempDateRange({...tempDateRange, start: e.target.value})}
                                    maxW="150px"
                                    bg="white"
                                />
                                <Box>〜</Box>
                                <Input
                                    type="date"
                                    value={tempDateRange.end}
                                    onChange={(e) => setTempDateRange({...tempDateRange, end: e.target.value})}
                                    maxW="150px"
                                    bg="white"
                                />
                                <Button size="sm" colorScheme="blue" onClick={handleApply}>
                                    適用
                                </Button>
                                <Button size="sm" variant="ghost" onClick={handleClear}>
                                    クリア
                                </Button>
                            </HStack>
                            <Box w="full">
                                <Grid
                                    templateColumns={['repeat(1, 1fr)', 'repeat(2, 1fr)', 'repeat(4, 1fr)']}
                                    gap={6}
                                    mt={1}
                                    width="100%" // ← 90% → 100% 推奨
                                >
                                    <Skeleton isLoaded={!isLoading} rounded='2xl' w='100%'>
                                        <SmallCardWithIcon data={`${metrics.followerCount.toLocaleString()}人`}
                                                           title='フォロワー数'>
                                            <ImUser size='30px'/>
                                        </SmallCardWithIcon>
                                    </Skeleton>
                                    <Skeleton isLoaded={!isLoading} rounded='2xl' w='100%'>
                                        <SmallCardWithIcon data={`${metrics.followingCount.toLocaleString()}人`}
                                                           title='フォロー中'>
                                            <ImUser size='30px'/>
                                        </SmallCardWithIcon>
                                    </Skeleton>
                                    <Skeleton isLoaded={!isLoading} rounded='2xl' w='100%'>
                                        <SmallCardWithIcon data={`${metrics.postCount.toLocaleString()}件`}
                                                           title='投稿数'>
                                            <ImUser size='30px'/>
                                        </SmallCardWithIcon>
                                    </Skeleton>
                                    <Skeleton isLoaded={!isLoading} rounded='2xl' w='100%'>
                                        <SmallCardWithIcon data={`${metrics.totalSaved.toLocaleString()}件`}
                                                           title='保存数'>
                                            <ImUser size='30px'/>
                                        </SmallCardWithIcon>
                                    </Skeleton>
                                    <Skeleton isLoaded={!isLoading} rounded='2xl' w='100%'>
                                        <SmallCardWithIcon data={`${metrics.totalLikes.toLocaleString()}件`}
                                                           title='いいね数'>
                                            <ImUser size='30px'/>
                                        </SmallCardWithIcon>
                                    </Skeleton>
                                    <Skeleton isLoaded={!isLoading} rounded='2xl' w='100%'>
                                        <SmallCardWithIcon data={`${metrics.totalComments.toLocaleString()}件`}
                                                           title='コメント数'>
                                            <ImUser size='30px'/>
                                        </SmallCardWithIcon>
                                    </Skeleton>
                                    <Skeleton isLoaded={!isLoading} rounded='2xl' w='100%'>
                                        <SmallCardWithIcon data={`${metrics.totalViews.toLocaleString()}回`}
                                                           title='合計再生回数'>
                                            <ImUser size='30px'/>
                                        </SmallCardWithIcon>
                                    </Skeleton>
                                </Grid>
                            </Box>
                            <Box w="full">
                                <Grid templateColumns={['1fr', '1fr', 'repeat(2, 1fr)']} gap={6}>
                                <GridItem>
                                        <Card>
                                            <CardHeader fontSize="md" fontWeight={"bold"} borderBottom={"1px"}
                                                        borderBottomColor={"#e2e2e2"}>アカウントへのリーチ数</CardHeader>
                                            <CardBody>
                                                <Text fontSize="sm">リーチ数の推移</Text>
                                                <Text fontSize="xl" fontWeight={"bold"}
                                                      padding={3}>{latestReach.toLocaleString()}</Text>
                                                <MetricLineChart
                                                    data={filteredMetricsHistory.map(item => ({
                                                        timestamp: item.timestamp,
                                                        value: item.total_reach
                                                    }))}
                                                    label="リーチ"
                                                    color="#3779fe"
                                                />
                                            </CardBody>
                                        </Card>
                                    </GridItem>

                                    <GridItem>
                                        <Card>
                                            <CardHeader fontSize="md" fontWeight={"bold"} borderBottom={"1px"}
                                                        borderBottomColor={"#e2e2e2"}>プロフィールへのアクセス</CardHeader>
                                            <CardBody>
                                                <Text fontSize="sm">アクセス数の推移</Text>
                                                <Text fontSize="xl" fontWeight={"bold"}
                                                      padding={3}>{latestImpressions.toLocaleString()}</Text>
                                                <MetricLineChart
                                                    data={filteredMetricsHistory.map(item => ({
                                                        timestamp: item.timestamp,
                                                        value: item.total_impressions
                                                    }))}
                                                    label="アクセス"
                                                    color="#3779fe"
                                                />
                                            </CardBody>
                                        </Card>
                                    </GridItem>

                                    <GridItem>
                                        <Card>
                                            <CardHeader fontSize="md" fontWeight={"bold"} borderBottom={"1px"}
                                                        borderBottomColor={"#e2e2e2"}>アカウントへのフォロー数</CardHeader>
                                            <CardBody>
                                                <Text fontSize="sm">フォロー数の推移</Text>
                                                <Text fontSize="xl" fontWeight={"bold"}
                                                      padding={3}>{latestFollowerCount.toLocaleString()}</Text>
                                                <MetricLineChart
                                                    data={filteredMetricsHistory.map(item => ({
                                                        timestamp: item.timestamp,
                                                        value: item.follower_count
                                                    }))}
                                                    label="フォロー"
                                                    color="#3779fe"
                                                />
                                            </CardBody>
                                        </Card>
                                    </GridItem>
                                </Grid>
                            </Box>
                        </VStack>

                    )}

                    {/* メディアタイプ別統計 */}
                    {/*{mediaTypeStats && loginMethod === 'facebook' && (*/}
                    {/*    <MediaTypeStatsPanel stats={mediaTypeStats}/>*/}
                    {/*)}*/}

                    {/* DMセクション - 復元部分 */}
                    {pageId && (
                        <Card>
                            <CardBody>
                                <Text fontWeight="bold" fontSize="lg" mb={4}>InstagramのDM</Text>
                                <Grid templateColumns="repeat(2, 1fr)" gap={6}>
                                    {/* 左側: 個別DM返信 */}
                                    <GridItem>
                                        <Box borderWidth="1px" borderRadius="md" p={4} bg="white">
                                            <HStack justify="space-between" mb={3}>
                                                <Text fontWeight="semibold">未読メッセージ</Text>
                                                <Button
                                                    size="sm"
                                                    colorScheme="blue"
                                                    variant="outline"
                                                    onClick={() => navigate(`/restaurants/${restaurantId}/instagram/dm-history`)}
                                                >
                                                    履歴を見る
                                                </Button>
                                            </HStack>
                                            <InstagramDMSection
                                                restaurantId={restaurantId || ''}
                                                instagramUsername={instagramUserInfo?.username || ''}
                                            />
                                        </Box>
                                    </GridItem>


                                    {/* 右側: DM一括返信機能 */}
                                    {/*<GridItem>*/}
                                    {/*    <Box borderWidth="1px" borderRadius="md" p={4} bg="white">*/}
                                    {/*        <VStack align="stretch" spacing={4}>*/}
                                    {/*            <Text fontWeight="semibold">DM一括返信</Text>*/}
                                    {/*            <Text fontSize="sm" color="gray.600">*/}
                                    {/*                ボタンをクリックして、フォロワーに一括でDMを送信します。*/}
                                    {/*            </Text>*/}

                                    {/*            <InstagramBulkDmSection restaurantId={restaurantId || ''}/>*/}
                                    {/*        </VStack>*/}
                                    {/*    </Box>*/}
                                    {/*</GridItem>*/}
                                </Grid>
                            </CardBody>
                        </Card>
                    )}


                    {/* InstagramContentsの代わりにInstagramPostsGridを使用 */}
                    {isAuthenticated && restaurantId && (
                        <Box mb={4}>
                            <Card>
                                <CardHeader>
                                    <HStack spacing={2}>
                                        <FaInstagram />
                                        <Text fontWeight="bold" fontSize="lg">コンテンツの概要</Text>
                                    </HStack>
                                </CardHeader>
                                <Tabs variant='soft-rounded' colorScheme="blue" isLazy w='100%' ml={[0, 2, 5]}>
                                {/*<Tabs variant="unstyled" isFitted={false} ml={[0, 2, 5]}>*/}
                                    <TabList justifyContent="start" gap={4} mb="1em">
                                        <Tab
                                            minW="150px"
                                            px={4}
                                            py={2}
                                        >
                                            すべて
                                        </Tab>
                                        <Tab
                                            minW="150px"
                                            px={4}
                                            py={2}
                                        >
                                            投稿
                                        </Tab>
                                        <Tab
                                            minW="150px"
                                            px={4}
                                            py={2}
                                        >
                                            ストーリーズ
                                        </Tab>
                                    </TabList>
                                    <TabPanels>
                                        {/* すべてタブ：総リーチの折れ線グラフ */}
                                        <TabPanel>
                                            <ResponsiveContainer width="100%" height={300}>
                                                <AreaChart data={metricsHistory}>
                                                    <defs>
                                                        <linearGradient id="colorStory" x1="0" y1="0" x2="0" y2="1">
                                                            <stop offset="5%" stopColor="#82ca9d" stopOpacity={0.8}/>
                                                            <stop offset="95%" stopColor="#82ca9d" stopOpacity={0}/>
                                                        </linearGradient>
                                                    </defs>
                                                    <CartesianGrid stroke="#e2e8f0" strokeDasharray="3 3"/>
                                                    <XAxis
                                                        dataKey="timestamp"
                                                        tickFormatter={(value) => {
                                                            const date = new Date(value);
                                                            return `${date.getMonth() + 1}月${date.getDate()}日`;
                                                        }}
                                                    />
                                                    <YAxis />
                                                    <Tooltip />
                                                    <Area
                                                        type="monotone"
                                                        dataKey="story_reach"
                                                        stroke="#82ca9d"
                                                        fillOpacity={1}
                                                        fill="url(#colorStory)"
                                                    />
                                                </AreaChart>
                                            </ResponsiveContainer>
                                        </TabPanel>

                                        {/* 投稿タブ */}
                                        <TabPanel>
                                            <SimpleGrid columns={[1, null, 2]} spacing={8}>
                                                {/* 左：月毎のインタラクション棒グラフ */}
                                                <Box>
                                                    <Text fontWeight="bold" mb={2}>1ヶ月ごとのインタラクション数</Text>
                                                    <ResponsiveContainer width="100%" height={300}>
                                                        <BarChart data={monthlyInteractions}>
                                                            <CartesianGrid strokeDasharray="3 3"/>
                                                            <XAxis dataKey="month"/>
                                                            <YAxis />
                                                            <Tooltip />
                                                            <Legend />
                                                            <Bar dataKey="interactions" fill="#8884d8"/>
                                                        </BarChart>
                                                    </ResponsiveContainer>
                                                </Box>

                                                {/* 右：メディアタイプ別のインタラクション棒グラフ */}
                                                <Box>
                                                    <Text fontWeight="bold"
                                                          mb={2}>メディアタイプ別インタラクション</Text>
                                                    <ResponsiveContainer width="100%" height={300}>
                                                        <BarChart data={mediaTypeChartData}>
                                                            <CartesianGrid strokeDasharray="3 3"/>
                                                            <XAxis dataKey="type"/>
                                                            <YAxis />
                                                            <Tooltip />
                                                            <Legend />
                                                            <Bar dataKey="likes" fill="#3182ce"/>
                                                            <Bar dataKey="comments" fill="#f56565"/>
                                                        </BarChart>
                                                    </ResponsiveContainer>
                                                </Box>
                                            </SimpleGrid>
                                        </TabPanel>

                                        {/* ストーリーズ：リーチの折れ線グラフ */}
                                        <TabPanel>
                                            <ResponsiveContainer width="100%" height={300}>
                                                <LineChart data={metricsHistory}>
                                                    <Line type="monotone" dataKey="story_reach" stroke="#82ca9d"/>
                                                    <CartesianGrid stroke="#ccc"/>
                                                    <XAxis
                                                        dataKey="timestamp"
                                                        tickFormatter={(value) => {
                                                            const date = new Date(value);
                                                            return `${date.getMonth() + 1}月${date.getDate()}日`;
                                                        }}
                                                    />
                                                    <YAxis />
                                                    <Tooltip />
                                                </LineChart>
                                            </ResponsiveContainer>
                                        </TabPanel>
                                    </TabPanels>
                                </Tabs>
                            </Card>
                        </Box>
                    )}

                </VStack>
            )}
            <InstagramPostsGrid
                mediaData={filteredMediaData}
                isLoading={isLoading}
                title="インスタグラム投稿一覧"
                onFilterChange={handleFilterChange}
                onDateRangeChange={handleDateRangeChange}
                isInstagramLogin={true}
                layoutMode="carousel"
            />

        </Box>
    );
};

export default Instagram;